import { useState, useEffect } from "react";
import { apiClient } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  Users, 
  Plus, 
  MoreVertical, 
  Edit, 
  Trash2, 
  Search,
  Filter,
  Phone,
  CreditCard
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";

const statusOptions = [
  { value: "available", label: "Available", className: "bg-green-500/20 text-green-400 border-green-500/30" },
  { value: "on_duty", label: "On Duty", className: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
  { value: "off_duty", label: "Off Duty", className: "bg-gray-500/20 text-gray-400 border-gray-500/30" }
];

export default function DriversPage() {
  const { user } = useAuth();
  const [drivers, setDrivers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  
  const [formData, setFormData] = useState({
    full_name: "",
    license_number: "",
    license_expiry: "",
    phone: "",
    organization_id: "",
    status: "available"
  });

  const canEdit = user?.role === "admin" || user?.role === "fleet_manager";

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [driversRes, orgsRes] = await Promise.all([
        apiClient.get("/drivers"),
        apiClient.get("/organizations")
      ]);
      setDrivers(driversRes.data);
      setOrganizations(orgsRes.data);
      
      if (orgsRes.data.length > 0 && !formData.organization_id) {
        setFormData(prev => ({ ...prev, organization_id: orgsRes.data[0].id }));
      }
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingDriver) {
        await apiClient.put(`/drivers/${editingDriver.id}`, formData);
        toast.success("Driver updated successfully");
      } else {
        await apiClient.post("/drivers", formData);
        toast.success("Driver added successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleEdit = (driver) => {
    setEditingDriver(driver);
    setFormData({
      full_name: driver.full_name,
      license_number: driver.license_number,
      license_expiry: driver.license_expiry,
      phone: driver.phone,
      organization_id: driver.organization_id,
      status: driver.status
    });
    setDialogOpen(true);
  };

  const handleDelete = async (driverId) => {
    if (!confirm("Are you sure you want to delete this driver?")) return;
    try {
      await apiClient.delete(`/drivers/${driverId}`);
      toast.success("Driver deleted successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete driver");
    }
  };

  const resetForm = () => {
    setEditingDriver(null);
    setFormData({
      full_name: "",
      license_number: "",
      license_expiry: "",
      phone: "",
      organization_id: organizations[0]?.id || "",
      status: "available"
    });
  };

  const filteredDrivers = drivers.filter(driver => {
    const matchesSearch = 
      driver.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      driver.license_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      driver.phone.includes(searchTerm);
    const matchesStatus = statusFilter === "all" || driver.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const statusConfig = statusOptions.find(s => s.value === status);
    return statusConfig || statusOptions[0];
  };

  const isLicenseExpiringSoon = (expiryDate) => {
    const expiry = new Date(expiryDate);
    const today = new Date();
    const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 90;
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="drivers-loading">
        <div className="skeleton-tactical h-8 w-48 rounded" />
        <div className="skeleton-tactical h-64 w-full rounded" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="drivers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight text-foreground">
            DRIVER ROSTER
          </h1>
          <p className="text-sm font-mono text-muted-foreground mt-1">
            Manage fleet drivers and assignments
          </p>
        </div>
        {canEdit && (
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-heading tracking-wider" data-testid="add-driver-btn">
                <Plus className="w-4 h-4 mr-2" />
                ADD DRIVER
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border rounded-sm max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading text-lg tracking-wide">
                  {editingDriver ? "EDIT DRIVER" : "ADD NEW DRIVER"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="driver-form">
                <div className="space-y-2">
                  <Label className="text-xs font-mono text-muted-foreground uppercase">Full Name</Label>
                  <Input
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    placeholder="Enter full name"
                    className="bg-input border-border rounded-sm font-mono text-sm"
                    required
                    data-testid="driver-name-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-mono text-muted-foreground uppercase">License Number</Label>
                    <Input
                      value={formData.license_number}
                      onChange={(e) => setFormData({ ...formData, license_number: e.target.value })}
                      placeholder="TZ-DL-123456"
                      className="bg-input border-border rounded-sm font-mono text-sm"
                      required
                      data-testid="driver-license-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-mono text-muted-foreground uppercase">License Expiry</Label>
                    <Input
                      type="date"
                      value={formData.license_expiry}
                      onChange={(e) => setFormData({ ...formData, license_expiry: e.target.value })}
                      className="bg-input border-border rounded-sm font-mono text-sm"
                      required
                      data-testid="driver-expiry-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-mono text-muted-foreground uppercase">Phone Number</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+255 712 345 678"
                    className="bg-input border-border rounded-sm font-mono text-sm"
                    required
                    data-testid="driver-phone-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-mono text-muted-foreground uppercase">Status</Label>
                  <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                    <SelectTrigger className="bg-input border-border rounded-sm font-mono text-sm" data-testid="driver-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border rounded-sm">
                      {statusOptions.map(option => (
                        <SelectItem key={option.value} value={option.value} className="font-mono text-sm">{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button type="submit" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-heading tracking-wider" data-testid="driver-submit-btn">
                  {editingDriver ? "UPDATE DRIVER" : "ADD DRIVER"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filters */}
      <Card className="bg-card border-border rounded-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search drivers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-input border-border rounded-sm font-mono text-sm"
                data-testid="driver-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48 bg-input border-border rounded-sm font-mono text-sm" data-testid="driver-filter-select">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border rounded-sm">
                <SelectItem value="all" className="font-mono text-sm">All Statuses</SelectItem>
                {statusOptions.map(option => (
                  <SelectItem key={option.value} value={option.value} className="font-mono text-sm">{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Drivers Table */}
      <Card className="bg-card border-border rounded-sm" data-testid="drivers-table">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">NAME</TableHead>
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">LICENSE</TableHead>
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">EXPIRY</TableHead>
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">PHONE</TableHead>
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">STATUS</TableHead>
                  {canEdit && <TableHead className="font-heading text-xs tracking-wide text-muted-foreground text-right">ACTIONS</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDrivers.map((driver) => {
                  const statusBadge = getStatusBadge(driver.status);
                  const licenseWarning = isLicenseExpiringSoon(driver.license_expiry);
                  return (
                    <TableRow key={driver.id} className="border-border hover:bg-accent/50" data-testid={`driver-row-${driver.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-sm bg-primary/10 border border-primary/30 flex items-center justify-center">
                            <Users className="w-4 h-4 text-primary" />
                          </div>
                          <span className="font-medium text-foreground">{driver.full_name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <CreditCard className="w-3 h-3 text-muted-foreground" />
                          <span className="font-mono text-sm text-muted-foreground">{driver.license_number}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={`font-mono text-sm ${licenseWarning ? "text-yellow-400" : "text-muted-foreground"}`}>
                          {driver.license_expiry}
                          {licenseWarning && " ⚠"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Phone className="w-3 h-3 text-muted-foreground" />
                          <span className="font-mono text-sm text-muted-foreground">{driver.phone}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${statusBadge.className} rounded-sm font-mono text-xs border`}>
                          {statusBadge.label}
                        </Badge>
                      </TableCell>
                      {canEdit && (
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`driver-actions-${driver.id}`}>
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-card border-border rounded-sm">
                              <DropdownMenuItem onClick={() => handleEdit(driver)} className="cursor-pointer">
                                <Edit className="w-4 h-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              {user?.role === "admin" && (
                                <DropdownMenuItem 
                                  onClick={() => handleDelete(driver.id)} 
                                  className="cursor-pointer text-destructive focus:text-destructive"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
                {filteredDrivers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={canEdit ? 6 : 5} className="text-center py-8 text-muted-foreground font-mono">
                      No drivers found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-heading font-bold text-primary">{drivers.length}</p>
            <p className="text-xs font-mono text-muted-foreground">TOTAL DRIVERS</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-heading font-bold text-green-400">{drivers.filter(d => d.status === "available").length}</p>
            <p className="text-xs font-mono text-muted-foreground">AVAILABLE</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-heading font-bold text-blue-400">{drivers.filter(d => d.status === "on_duty").length}</p>
            <p className="text-xs font-mono text-muted-foreground">ON DUTY</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-heading font-bold text-yellow-400">
              {drivers.filter(d => isLicenseExpiringSoon(d.license_expiry)).length}
            </p>
            <p className="text-xs font-mono text-muted-foreground">LICENSE EXPIRING</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
